import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../../api';
import type { ComponentRepositoryAccess, GitConnectionVerification, GitProvider, RepositoryAccess } from '../../api';
import { Link } from 'react-router-dom';
import type { Resource } from '../../hooks';
import type { GitConnectionResponse } from '../../api';
import { ConfirmDialog, ErrorNotice, Field, LoadingRows, StatusBadge } from '../ui';
import { Callout, Input, Select } from '../primitives';
import { ConfigurationDrawer } from './ConfigurationDrawer';
import { FactList, PanelCard, SectionHeader } from './ui';

const verificationReasonLabels: Record<string, string> = {
  invalid_token: 'The provider rejected the token — it may be expired or revoked.',
  repository_not_accessible: 'The configured repository does not exist, or this token cannot access it.',
  rate_limited: 'The provider is rate-limiting this token right now — try again shortly.',
  unexpected_error: 'The provider returned an unexpected response.',
};

const gitProviderHosts: Record<GitProvider, string> = {
  github: 'github.com',
  gitlab: 'gitlab.com',
  bitbucket: 'bitbucket.org',
};

/**
 * RepositoryAccessCard says in one line how Commerce Ops reads this
 * project's repositories ("GitHub App · All 3 repositories accessible",
 * CO-159); a warning appears only when something cannot be read. Which
 * credential reads each repository is behind "Credential details"; checking,
 * replacing or removing the token is in "Manage access". A permanently open
 * secret field on the page would be noise and a nudge to paste a credential
 * nobody asked for.
 */
export function RepositoryAccessCard({
  projectId,
  connection,
  editRequest = 0,
  access,
  onChanged,
}: {
  projectId: string;
  connection: Resource<GitConnectionResponse>;
  /** Which credential is in use, by the checkout's own rules (CO-98). */
  access?: RepositoryAccess | null;
  onChanged?: () => void;
  /** Bumped by the page to open the access form (for example from the
   * "Add frontend" dialog when the project has no repository access). */
  editRequest?: number;
}) {
  const [provider, setProvider] = useState<GitProvider>('github');
  const [account, setAccount] = useState('');
  const [token, setToken] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [verifyResult, setVerifyResult] = useState<GitConnectionVerification | null>(null);
  const [verifyError, setVerifyError] = useState<Error | null>(null);
  useEffect(() => {
    if (editRequest > 0) setEditing(true);
  }, [editRequest]);

  // Reflect the server's stored account, never a client-invented guess.
  useEffect(() => {
    const stored = connection.data?.connection;
    if (!stored) return;
    setProvider(stored.provider);
    setAccount(stored.account);
  }, [connection.data]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.configureGitConnection(projectId, { provider, account: account.trim(), token });
      setToken('');
      setEditing(false);
      connection.reload();
      onChanged?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    setRemoving(true);
    setError(null);
    try {
      await api.deleteGitConnection(projectId);
      setConfirmRemove(false);
      setAccount('');
      setToken('');
      setVerifyResult(null);
      connection.reload();
      onChanged?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setRemoving(false);
    }
  }

  async function verify() {
    setVerifying(true);
    setVerifyError(null);
    setVerifyResult(null);
    try {
      setVerifyResult(await api.verifyGitConnection(projectId));
    } catch (cause) {
      setVerifyError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setVerifying(false);
    }
  }

  const configured = connection.data?.configured ?? false;
  const stored = connection.data?.connection;
  // Whether Commerce Ops can read the repositories comes from the access
  // resolution, never from the token alone: with the organization's GitHub
  // App a project token is optional (observation 9).
  const readable = access ? (access.readable ?? (access.source === 'project_connection' || access.source === 'github_app')) : configured;
  const repositories = repositoryRows(access);
  const unreadable = repositories.filter((row) => row.source === 'none');
  const partly = unreadable.length > 0 && unreadable.length < repositories.length;
  const closeDrawer = () => {
    setEditing(false);
    setToken('');
  };

  return (
    <PanelCard labelledBy="repo-access" testId="repository-access">
      <SectionHeader
        id="repo-access"
        title="Repository access"
        actions={
          // When nothing can be read, the warning below carries the action.
          readable || configured || !connection.data ? (
            <button type="button" className="button button--secondary" onClick={() => setEditing(true)} data-testid="repository-access-edit">
              Manage access
            </button>
          ) : null
        }
      />

      {access && access.source !== 'no_repository' ? (
        <p className="summary-line" data-testid="repository-access-in-use">
          <StatusBadge
            kind="repositoryAccess"
            value={readable ? 'configured' : 'not_configured'}
            label={readable ? 'Readable' : partly ? 'Partly readable' : 'No credential'}
          />
          <span>{accessSummary(repositories, stored ? `${gitProviderHosts[stored.provider]}/${stored.account}` : null)}</span>
        </p>
      ) : null}
      {connection.loading && !connection.data ? <LoadingRows label="Loading repository access" rows={2} rowHeight="28px" /> : null}
      <ErrorNotice error={connection.error} />
      <ErrorNotice error={error} />

      {connection.data && !readable ? (
        <Callout
          tone="warning"
          testId="repository-access-empty"
          actions={
            <button type="button" className="button button--secondary button--small" onClick={() => setEditing(true)} data-testid={configured ? undefined : 'repository-access-edit'}>
              {configured ? 'Manage access' : 'Configure access'}
            </button>
          }
        >
          {partly
            ? `Commerce Ops cannot read ${unreadable.map((row) => row.name).join(', ')} yet. Add a token that reads ${unreadable.length === 1 ? 'that repository' : 'those repositories'}.`
            : 'Commerce Ops cannot read this project’s repositories yet. With the organization’s GitHub App installed, github.com repositories work without a token here.'}
        </Callout>
      ) : null}

      {repositories.length > 0 ? (
        <details className="quiet-disclosure" data-testid="repository-access-details">
          <summary>Credential details</summary>
          <ul className="repo-access-list" aria-label="Repositories and the credential that reads each" data-testid="repository-access-list">
            {repositories.map((row) => (
              <li key={row.key} className="repo-access-list__item">
                <span className="repo-access-list__name">{row.name}</span>
                <code className="repo-access-list__url">{row.url}</code>
                <StatusBadge kind="repositoryAccess" value={row.source === 'no_repository' ? 'none' : row.source} />
              </li>
            ))}
          </ul>
          {access?.other ? <p className="field__help">{access.other}</p> : null}
        </details>
      ) : null}

      <ConfigurationDrawer
        open={editing}
        title="Manage repository access"
        description="Read access is enough. A token is stored encrypted and never shown again."
        onClose={closeDrawer}
        testId="repository-access-drawer"
      >
        {access?.source === 'github_app' || repositories.some((row) => row.source === 'github_app') ? (
          <p className="panel__note">
            The organization’s GitHub App reads the github.com repositories it was granted.{' '}
            <Link to="/app/settings?tab=integrations">Manage the GitHub App</Link>
          </p>
        ) : null}
        {configured && stored ? (
          <>
            <FactList
              facts={[
                { label: 'Project token', value: <StatusBadge kind="repositoryAccess" value="configured" /> },
                { label: 'Account', value: <code>{gitProviderHosts[stored.provider]}/{stored.account}</code> },
                {
                  label: 'Token',
                  value: stored.secret_last_four ? (
                    <>
                      ending <code>{stored.secret_last_four}</code>
                    </>
                  ) : (
                    <span className="muted">Stored</span>
                  ),
                },
                { label: 'Last changed', value: `by ${stored.created_by} on ${new Date(stored.updated_at).toLocaleString()}` },
              ]}
            />
            <div className="pill-list">
              <button type="button" className="button button--secondary" onClick={verify} disabled={verifying}>
                {verifying ? 'Checking' : 'Check access'}
              </button>
              <button type="button" className="button button--danger-quiet" onClick={() => setConfirmRemove(true)} disabled={removing}>
                Remove token
              </button>
            </div>
            <ErrorNotice error={verifyError} />
            {verifyResult ? (
              verifyResult.verified ? (
                <p className="summary-line" role="status">
                  <StatusBadge kind="repositoryAccess" value="works" />
                  <span className="muted">
                    Default branch <code>{verifyResult.default_branch}</code>
                    {verifyResult.private ? ' · private repository' : ' · public repository'}
                  </span>
                </p>
              ) : (
                <p className="summary-line" role="status">
                  <StatusBadge kind="repositoryAccess" value="failed" />
                  <span className="muted">
                    {(verifyResult.reason && verificationReasonLabels[verifyResult.reason]) ?? 'Verification failed.'}
                  </span>
                </p>
              )
            ) : null}
          </>
        ) : readable ? (
          <p className="panel__note muted" data-testid="repository-access-token-optional">
            No project token is needed: every repository is read without one. Add one only for a repository the GitHub
            App cannot read, such as one on GitLab or Bitbucket.
          </p>
        ) : null}
        <form className="form" onSubmit={save} id="repository-access-form">
          <h3 className="section-header__title">{configured ? 'Replace the token' : 'Add a project token'}</h3>
          <Field label="Provider">
            <Select value={provider} onChange={(event) => setProvider(event.target.value as GitProvider)}>
              <option value="github">GitHub</option>
              <option value="gitlab">GitLab</option>
              <option value="bitbucket">Bitbucket</option>
            </Select>
          </Field>
          <Field label="Account or organization" hint="The account that owns the repository.">
            <Input required value={account} onChange={(event) => setAccount(event.target.value)} placeholder="acme" autoComplete="off" spellCheck={false} />
          </Field>
          <Field
            label="Personal access token"
            hint="GitHub: repo scope for private repositories; GitLab: read_repository; Bitbucket: an app password with repository read."
          >
            <Input
              required
              type="password"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              autoComplete="off"
            />
          </Field>
          <ErrorNotice error={error} />
          <div className="pill-list">
            <button className="button button--primary" type="submit" disabled={saving || account.trim() === '' || token === ''}>
              {saving ? 'Saving' : configured ? 'Replace token' : 'Save token'}
            </button>
            <button type="button" className="button button--secondary" onClick={closeDrawer}>
              Cancel
            </button>
          </div>
        </form>
      </ConfigurationDrawer>
      <ConfirmDialog
        open={confirmRemove}
        title="Remove the project token?"
        description="Commerce Ops stops using it at once. A repository only this token can read cannot be deployed until a new token is added."
        confirmLabel="Remove token"
        danger
        busy={removing}
        onConfirm={() => void remove()}
        onCancel={() => setConfirmRemove(false)}
      />
    </PanelCard>
  );
}

/**
 * "GitHub App · All 3 repositories accessible", from each repository's own
 * credential. Components that share a repository (a monorepo) count once.
 */
function accessSummary(rows: RepositoryRow[], tokenAccount: string | null): string {
  if (rows.length === 0) return 'No repository to read yet';
  const reads = (source: RepositoryRow['source']) => source === 'github_app' || source === 'project_connection';
  const byUrl = new Map<string, RepositoryRow['source']>();
  for (const row of rows) if (!byUrl.has(row.url) || !reads(byUrl.get(row.url)!)) byUrl.set(row.url, row.source);
  const total = byUrl.size;
  const readableCount = [...byUrl.values()].filter(reads).length;
  const sources = [...new Set([...byUrl.values()].filter(reads))];
  const names = sources.map((source) => (source === 'github_app' ? 'GitHub App' : tokenAccount ? `Project token (${tokenAccount})` : 'Project token'));
  const count =
    readableCount === total
      ? total === 1
        ? 'The repository is accessible'
        : `All ${total} repositories accessible`
      : `${readableCount} of ${total} repositories accessible`;
  return names.length > 0 ? `${names.join(' and ')} · ${count}` : count;
}

interface RepositoryRow {
  key: string;
  name: string;
  url: string;
  source: ComponentRepositoryAccess['source'];
}

/** The project's repository and each component's, once per repository. */
function repositoryRows(access: RepositoryAccess | null | undefined): RepositoryRow[] {
  if (!access) return [];
  const rows: RepositoryRow[] = (access.components ?? []).map((c) => ({ key: c.application_id, name: c.application_name, url: c.repository_url, source: c.source }));
  if (access.repository_url && !rows.some((row) => row.url === access.repository_url)) {
    rows.unshift({ key: 'project', name: 'Project repository', url: access.repository_url, source: access.source });
  }
  return rows;
}
